import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import { Lead } from '../../models/Outreach/types'

const invalidatePipelineQueries = (
    queryClient: ReturnType<typeof useQueryClient>,
) => {
    queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
    queryClient.invalidateQueries({
        queryKey: ['leasing-season-pipeline-counts'],
        exact: false,
    })
}

export interface InviteToApplyResult {
    invitation_id: string
    register_url: string
    sent_to: string
    lead: Lead
}

export function useAccountInvite() {
    const queryClient = useQueryClient()

    const inviteMutation = useMutation({
        mutationFn: async ({
            leadId,
            email,
            expiresAt,
        }: {
            leadId: number
            email?: string
            expiresAt?: string
        }) => {
            const { data } = await axiosInstance.post<InviteToApplyResult>(
                `/outreach/leads/${leadId}/invite-to-apply/`,
                {
                    ...(email ? { email } : {}),
                    ...(expiresAt ? { expires_at: expiresAt } : {}),
                },
            )
            return data
        },
        onSuccess: () => {
            invalidatePipelineQueries(queryClient)
            toast.success('Account invite sent')
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ?? 'Failed to send account invite',
            )
        },
    })

    const resendMutation = useMutation({
        mutationFn: async ({
            leadId,
            email,
        }: {
            leadId: number
            email?: string
        }) => {
            const { data } = await axiosInstance.post<InviteToApplyResult>(
                `/outreach/leads/${leadId}/resend-account-invite/`,
                email ? { email } : {},
            )
            return data
        },
        onSuccess: () => {
            invalidatePipelineQueries(queryClient)
            toast.success('Account invite resent')
        },
        onError: (error: { response?: { data?: { error?: string } } }) => {
            toast.error(
                error?.response?.data?.error ??
                    'Failed to resend account invite',
            )
        },
    })

    return {
        inviteToApply: inviteMutation.mutateAsync,
        resendAccountInvite: resendMutation.mutateAsync,
        isInviting: inviteMutation.isLoading,
        isResending: resendMutation.isLoading,
    }
}
